import { useGetProfile, useGetUsage, type StudySession } from "@workspace/api-client-react";
import { useAuth } from "@/lib/auth-context";
import { Link, useLocation } from "wouter";
import { Button, Card, Badge, Input, Label } from "@/components/ui";
import {
  PlusCircle, FileText, File, Type, ArrowRight, Loader2,
  BookOpen, Layers, MessageSquare, Zap, Crown,
  Folder, FolderOpen, FolderPlus, ChevronRight, Home, Trash2, X,
  MoreHorizontal, Pencil, FolderInput, GripVertical,
  Youtube, Video, Music,
} from "lucide-react";
import { formatDate } from "@/lib/utils";
import { API_BASE } from "@/lib/api";
import { useState, useEffect, useRef, useCallback } from "react";
import { UpgradeModal } from "@/components/upgrade-modal";
import {
  useFolders,
  useFolderSessions,
  useFolderAncestors,
  useCreateFolder,
  useDeleteFolder,
  useRenameFolder,
  useRenameSession,
  useMoveSession,
  useDeleteSessionFromDashboard,
  useAllFolders,
  type FolderItem,
} from "@/hooks/use-folders";

function UsageBar({ label, used, limit, color = "bg-primary" }: { label: string; used: number; limit: number; color?: string }) {
  const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const isNearLimit = pct >= 80;
  const isAtLimit = pct >= 100;

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium text-foreground">{label}</span>
        <span className={`text-xs font-semibold ${isAtLimit ? "text-destructive" : isNearLimit ? "text-amber-500 dark:text-amber-400" : "text-muted-foreground"}`}>
          {used.toLocaleString()} / {limit.toLocaleString()}
        </span>
      </div>
      <div className="w-full h-2.5 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${isAtLimit ? "bg-destructive" : isNearLimit ? "bg-amber-500" : color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function ConfirmDeleteModal({
  open,
  onClose,
  onConfirm,
  title,
  message,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  isPending: boolean;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <Card className="w-full max-w-sm p-6 shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-destructive/10 rounded-lg flex items-center justify-center">
              <Trash2 className="w-4 h-4 text-destructive" />
            </div>
            <h2 className="text-lg font-bold font-display">{title}</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors rounded-lg p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-5">{message}</p>
        <div className="flex gap-3">
          <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button type="button" variant="destructive" className="flex-1" onClick={onConfirm} disabled={isPending}>
            {isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Delete
          </Button>
        </div>
      </Card>
    </div>
  );
}

function RenameModal({
  open,
  onClose,
  onConfirm,
  currentName,
  label,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (name: string) => void;
  currentName: string;
  label: string;
  isPending: boolean;
}) {
  const [name, setName] = useState(currentName);
  useEffect(() => { if (open) setName(currentName); }, [open, currentName]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <Card className="w-full max-w-sm p-6 shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Pencil className="w-4 h-4 text-primary" />
            </div>
            <h2 className="text-lg font-bold font-display">Rename {label}</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors rounded-lg p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) onConfirm(name.trim()); }} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="rename-input">Name</Label>
            <Input id="rename-input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="flex-1" disabled={!name.trim() || isPending}>
              {isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function MoveToFolderModal({
  open,
  onClose,
  onConfirm,
  folders,
  currentFolderId,
  isPending,
  sessionTitle,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (folderId: number | null) => void;
  folders: FolderItem[];
  currentFolderId: number | null;
  isPending: boolean;
  sessionTitle: string;
}) {
  const [selected, setSelected] = useState<number | null>(null);

  useEffect(() => { if (open) setSelected(null); }, [open]);

  if (!open) return null;

  const availableFolders = folders.filter(f => f.id !== currentFolderId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <Card className="w-full max-w-sm p-6 shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <FolderInput className="w-4 h-4 text-primary" />
            </div>
            <h2 className="text-lg font-bold font-display">Move File</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors rounded-lg p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-4 line-clamp-1">
          Moving &ldquo;{sessionTitle}&rdquo;
        </p>
        {availableFolders.length === 0 && currentFolderId === null ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No folders available. Create a folder first.</p>
        ) : (
          <div className="space-y-1.5 max-h-60 overflow-y-auto mb-4">
            {currentFolderId !== null && (
              <button
                onClick={() => setSelected(-1)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl text-left text-sm font-medium transition-colors ${selected === -1 ? "bg-primary/10 text-primary border border-primary/30" : "hover:bg-muted border border-transparent"}`}
              >
                <Home className="w-4 h-4 flex-shrink-0" />
                Root (no folder)
              </button>
            )}
            {availableFolders.map((f) => (
              <button
                key={f.id}
                onClick={() => setSelected(f.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl text-left text-sm font-medium transition-colors ${selected === f.id ? "bg-primary/10 text-primary border border-primary/30" : "hover:bg-muted border border-transparent"}`}
              >
                <Folder className="w-4 h-4 text-amber-500 flex-shrink-0" />
                {f.name}
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-3">
          <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button
            type="button"
            className="flex-1"
            disabled={selected === null || isPending}
            onClick={() => onConfirm(selected === -1 ? null : selected)}
          >
            {isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Move
          </Button>
        </div>
      </Card>
    </div>
  );
}

function CreateFolderModal({
  open,
  onClose,
  parentId,
}: {
  open: boolean;
  onClose: () => void;
  parentId: number | null;
}) {
  const [name, setName] = useState("");
  const { mutate: createFolder, isPending } = useCreateFolder();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    createFolder(
      { name: name.trim(), parentId },
      {
        onSuccess: () => {
          setName("");
          onClose();
        },
      }
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <Card className="w-full max-w-sm p-6 shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <FolderPlus className="w-4 h-4 text-primary" />
            </div>
            <h2 className="text-lg font-bold font-display">New Folder</h2>
          </div>
          <button
            onClick={() => { setName(""); onClose(); }}
            className="text-muted-foreground hover:text-foreground transition-colors rounded-lg p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="folder-name">Folder name</Label>
            <Input
              id="folder-name"
              placeholder="e.g., Hematology"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="flex gap-3 pt-1">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => { setName(""); onClose(); }}
            >
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={!name.trim() || isPending}>
              {isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function ActionMenu({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return (
    <div ref={ref} className="absolute top-10 right-2 z-20 bg-card border border-border rounded-xl shadow-xl py-1.5 min-w-[140px] animate-in fade-in zoom-in-95 duration-150">
      {children}
    </div>
  );
}

function ActionMenuItem({ icon: Icon, label, onClick, destructive }: { icon: any; label: string; onClick: () => void; destructive?: boolean }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm font-medium transition-colors ${destructive ? "text-destructive hover:bg-destructive/10" : "text-foreground hover:bg-muted"}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

function FolderCard({
  folder,
  onClick,
  onDelete,
  onRename,
  isDragOver,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  folder: FolderItem;
  onClick: () => void;
  onDelete: () => void;
  onRename: () => void;
  isDragOver: boolean;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <Card
      className={`p-5 flex flex-col hover:shadow-lg hover:border-primary/30 transition-all cursor-pointer group relative ${isDragOver ? "ring-2 ring-primary border-primary bg-primary/5" : ""}`}
      onClick={onClick}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <button
        className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground p-1.5 rounded-lg hover:bg-muted z-10"
        onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {menuOpen && (
        <ActionMenu onClose={() => setMenuOpen(false)}>
          <ActionMenuItem icon={Pencil} label="Rename" onClick={() => { setMenuOpen(false); onRename(); }} />
          <ActionMenuItem icon={Trash2} label="Delete" onClick={() => { setMenuOpen(false); onDelete(); }} destructive />
        </ActionMenu>
      )}
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${isDragOver ? "bg-primary/20" : "bg-amber-500/10 group-hover:bg-amber-500/20"}`}>
          <Folder className={`w-5 h-5 ${isDragOver ? "text-primary" : "text-amber-500"}`} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold font-display line-clamp-1 group-hover:text-primary transition-colors pr-8">
            {folder.name}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {folder.itemCount === 0
              ? "Empty"
              : `${folder.itemCount} item${folder.itemCount !== 1 ? "s" : ""}`}
          </p>
        </div>
      </div>
    </Card>
  );
}

function SessionCard({
  session,
  onDelete,
  onRename,
  onMove,
  getSourceIcon,
}: {
  session: StudySession;
  onDelete: () => void;
  onRename: () => void;
  onMove: () => void;
  getSourceIcon: (type: string) => React.ReactNode;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [, setLocation] = useLocation();

  return (
    <Card
      className="p-6 h-full flex flex-col hover:shadow-xl hover:border-primary/30 transition-all cursor-pointer group relative"
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("session-id", String(session.id));
        e.dataTransfer.effectAllowed = "move";
      }}
      onClick={() => setLocation(`/sessions/${session.id}`)}
    >
      <button
        className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground p-1.5 rounded-lg hover:bg-muted z-10"
        onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {menuOpen && (
        <ActionMenu onClose={() => setMenuOpen(false)}>
          <ActionMenuItem icon={Pencil} label="Rename" onClick={() => { setMenuOpen(false); onRename(); }} />
          <ActionMenuItem icon={FolderInput} label="Move" onClick={() => { setMenuOpen(false); onMove(); }} />
          <ActionMenuItem icon={Trash2} label="Delete" onClick={() => { setMenuOpen(false); onDelete(); }} destructive />
        </ActionMenu>
      )}
      <div className="flex justify-between items-start mb-4">
        <Badge variant="outline" className="gap-1 bg-background">
          {getSourceIcon(session.sourceType)}
          <span className="uppercase tracking-wider">{session.sourceType}</span>
        </Badge>
        <span className="text-xs text-muted-foreground font-medium pr-8">{formatDate(session.createdAt)}</span>
      </div>
      <h3 className="text-lg font-bold font-display line-clamp-2 mb-2 group-hover:text-primary transition-colors">
        {session.title}
      </h3>
      {session.uploadedFileName && (
        <p className="text-sm text-muted-foreground truncate mb-4">{session.uploadedFileName}</p>
      )}
      <div className="mt-auto pt-4 flex items-center gap-2">
        <Badge variant={session.hasOutputs ? "default" : "accent"} className="text-[10px]">
          {session.hasOutputs ? "Ready to study" : "Needs generation"}
        </Badge>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const { data: profile, isLoading } = useGetProfile();
  const { data: usage, isLoading: usageLoading, refetch: refetchUsage } = useGetUsage();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [checkoutSynced, setCheckoutSynced] = useState(false);

  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<{ id: number; name: string }[]>([]);

  const [deleteTarget, setDeleteTarget] = useState<{ type: "file" | "folder"; id: number; name: string; itemCount?: number } | null>(null);
  const [renameTarget, setRenameTarget] = useState<{ type: "file" | "folder"; id: number; name: string } | null>(null);
  const [moveTarget, setMoveTarget] = useState<{ id: number; title: string; currentFolderId: number | null } | null>(null);
  const [dragOverFolderId, setDragOverFolderId] = useState<number | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutId = params.get("checkout_id");
    const isCheckoutSuccess = params.get("checkout") === "success";

    if (!isCheckoutSuccess || !checkoutId || checkoutSynced) return;

    setCheckoutSynced(true);

    const token = localStorage.getItem("studyflow_token");
    fetch(`${API_BASE}/api/subscription/sync-checkout`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      body: JSON.stringify({ checkoutId }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.synced) {
          refetchUsage();
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, "", cleanUrl);
        }
      })
      .catch(() => {});
  }, [checkoutSynced, refetchUsage]);

  const { data: folders = [], isLoading: foldersLoading } = useFolders(currentFolderId);
  const { data: folderSessions = [], isLoading: sessionsLoading } = useFolderSessions(currentFolderId);
  const { data: allFolders = [] } = useAllFolders();
  const { mutate: deleteFolder, isPending: deleteFolderPending } = useDeleteFolder();
  const { mutate: renameFolder, isPending: renameFolderPending } = useRenameFolder();
  const { mutate: renameSession, isPending: renameSessionPending } = useRenameSession();
  const { mutate: moveSession, isPending: moveSessionPending } = useMoveSession();
  const { mutate: deleteSession, isPending: deleteSessionPending } = useDeleteSessionFromDashboard();

  const contentLoading = foldersLoading || sessionsLoading;

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const getSourceIcon = (type: string) => {
    switch (type) {
      case "pdf":     return <File className="w-4 h-4" />;
      case "txt":     return <FileText className="w-4 h-4" />;
      case "youtube": return <Youtube className="w-4 h-4" />;
      case "video":   return <Video className="w-4 h-4" />;
      case "audio":   return <Music className="w-4 h-4" />;
      default:        return <Type className="w-4 h-4" />;
    }
  };

  const planLabels: Record<string, string> = { free: "Free", weekly: "Weekly", monthly: "Monthly", yearly: "Yearly" };
  const planLabel = planLabels[usage?.plan ?? "free"] ?? "Free";
  const isPaidPlan = usage?.plan !== "free" && usage?.plan !== undefined;
  const planColor = isPaidPlan
    ? "bg-primary/10 text-primary border-primary/20"
    : "bg-muted text-muted-foreground border-border";

  const handleOpenFolder = (folder: FolderItem) => {
    setBreadcrumbs((prev) => [...prev, { id: folder.id, name: folder.name }]);
    setCurrentFolderId(folder.id);
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      setBreadcrumbs([]);
      setCurrentFolderId(null);
    } else {
      const crumb = breadcrumbs[index];
      setBreadcrumbs((prev) => prev.slice(0, index + 1));
      setCurrentFolderId(crumb.id);
    }
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === "folder") {
      deleteFolder({ id: deleteTarget.id, parentId: currentFolderId }, { onSuccess: () => setDeleteTarget(null) });
    } else {
      deleteSession({ id: deleteTarget.id }, { onSuccess: () => setDeleteTarget(null) });
    }
  };

  const handleConfirmRename = (newName: string) => {
    if (!renameTarget) return;
    if (renameTarget.type === "folder") {
      renameFolder({ id: renameTarget.id, name: newName, parentId: currentFolderId }, { onSuccess: () => setRenameTarget(null) });
    } else {
      renameSession({ id: renameTarget.id, title: newName }, { onSuccess: () => setRenameTarget(null) });
    }
  };

  const handleConfirmMove = (folderId: number | null) => {
    if (!moveTarget) return;
    moveSession({ id: moveTarget.id, folderId }, { onSuccess: () => setMoveTarget(null) });
  };

  const handleDragOverFolder = (e: React.DragEvent, folderId: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverFolderId(folderId);
  };

  const handleDropOnFolder = (e: React.DragEvent, folderId: number) => {
    e.preventDefault();
    setDragOverFolderId(null);
    const sessionId = e.dataTransfer.getData("session-id");
    if (sessionId) {
      moveSession({ id: parseInt(sessionId), folderId });
    }
  };

  const newSessionHref = currentFolderId
    ? `/sessions/new?folderId=${currentFolderId}`
    : "/sessions/new";

  const hasContent = folders.length > 0 || folderSessions.length > 0;
  const isAtRoot = currentFolderId === null;

  const getDeleteMessage = () => {
    if (!deleteTarget) return "";
    if (deleteTarget.type === "file") {
      return `This will permanently delete "${deleteTarget.name}" and all its study materials (flashcards, MCQs, summaries, chat history). This action cannot be undone.`;
    }
    if (deleteTarget.itemCount && deleteTarget.itemCount > 0) {
      return `This folder contains ${deleteTarget.itemCount} item${deleteTarget.itemCount !== 1 ? "s" : ""}. Deleting it will move all files back to root and remove the folder. This cannot be undone.`;
    }
    return `This will permanently delete the folder "${deleteTarget.name}". This action cannot be undone.`;
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-3xl font-bold font-display">Welcome back, {user?.name?.split(' ')[0]} 👋</h1>
            {usage && (
              <Badge variant="outline" className={`${planColor} font-semibold`}>
                {isPaidPlan ? <Crown className="w-3 h-3 mr-1" /> : null}
                {planLabel}
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground mt-1 text-lg">Ready to crush your next study session?</p>
        </div>
        <div className="flex gap-3">
          {!isPaidPlan && (
            <Button variant="outline" className="rounded-xl" onClick={() => setShowUpgrade(true)}>
              <Zap className="mr-2 w-4 h-4" />
              Upgrade
            </Button>
          )}
          <Link href={newSessionHref}>
            <Button className="rounded-xl h-12 shadow-lg shadow-primary/25 hover:-translate-y-0.5 w-full sm:w-auto">
              <PlusCircle className="mr-2 w-5 h-5" />
              New Session
            </Button>
          </Link>
        </div>
      </div>

      {/* Usage + Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {usage && !usageLoading && (
          <Card className="p-6 lg:col-span-1">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display font-bold text-lg">Usage This Month</h3>
              {!isPaidPlan && (
                <button onClick={() => setShowUpgrade(true)} className="text-xs font-semibold text-primary hover:underline">
                  Upgrade
                </button>
              )}
            </div>
            {usage.plan === "free" ? (
              <div className="space-y-5">
                <UsageBar label="Files" used={usage.filesUsed} limit={usage.limits.monthlyFileLimit} color="bg-emerald-500" />
                <UsageBar label="Lerni Bot Messages" used={usage.chatMessagesUsed} limit={usage.limits.monthlyChatLimit} color="bg-purple-500" />
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Files uploaded</span>
                  <span className="font-semibold">{usage.filesUsed}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Lerni Bot messages</span>
                  <span className="font-semibold">{usage.chatMessagesUsed}</span>
                </div>
                <p className="text-xs text-primary font-medium pt-1">Unlimited study usage</p>
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-4">
              Resets {new Date(usage.billingCycleEnd).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            </p>
          </Card>
        )}

        <div className={`grid grid-cols-2 gap-4 ${usage ? "lg:col-span-2" : "lg:col-span-3"}`}>
          {[
            { label: "Total Sessions", value: profile?.stats.totalSessions || 0, icon: Layers, color: "text-blue-500", bg: "bg-blue-500/10" },
            { label: "Flashcards Created", value: profile?.stats.totalFlashcards || 0, icon: BookOpen, color: "text-indigo-500", bg: "bg-indigo-500/10" },
            { label: "Questions Answered", value: profile?.stats.totalMcqs || 0, icon: PlusCircle, color: "text-emerald-500", bg: "bg-emerald-500/10" },
            { label: "Lerni Bot Messages", value: profile?.stats.totalChatMessages || 0, icon: MessageSquare, color: "text-purple-500", bg: "bg-purple-500/10" },
          ].map((stat, i) => (
            <Card key={i} className="p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.bg}`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{stat.label}</p>
                  <p className="text-2xl font-bold text-foreground font-display">{stat.value}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Files & Folders Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold font-display">My Files</h2>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowCreateFolder(true)}
              className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
            >
              <FolderPlus className="w-4 h-4" />
              Add Folder
            </button>
            <Link href="/history" className="text-sm font-semibold text-primary hover:underline flex items-center">
              View all <ArrowRight className="ml-1 w-4 h-4" />
            </Link>
          </div>
        </div>

        {!isAtRoot && (
          <nav className="flex items-center gap-1 text-sm mb-5 flex-wrap">
            <button
              onClick={() => handleBreadcrumbClick(-1)}
              className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors font-medium"
            >
              <Home className="w-3.5 h-3.5" />
              Home
            </button>
            {breadcrumbs.map((crumb, i) => (
              <span key={crumb.id} className="flex items-center gap-1">
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50" />
                {i === breadcrumbs.length - 1 ? (
                  <span className="flex items-center gap-1 text-foreground font-semibold">
                    <FolderOpen className="w-3.5 h-3.5 text-amber-500" />
                    {crumb.name}
                  </span>
                ) : (
                  <button
                    onClick={() => handleBreadcrumbClick(i)}
                    className="text-muted-foreground hover:text-foreground transition-colors font-medium"
                  >
                    {crumb.name}
                  </button>
                )}
              </span>
            ))}
          </nav>
        )}

        {contentLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : !hasContent ? (
          <Card className="p-12 flex flex-col items-center justify-center text-center border-dashed">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
              {isAtRoot ? (
                <BookOpen className="w-8 h-8 text-muted-foreground opacity-50" />
              ) : (
                <Folder className="w-8 h-8 text-muted-foreground opacity-50" />
              )}
            </div>
            <h3 className="text-lg font-bold font-display">
              {isAtRoot ? "No sessions yet" : "This folder is empty"}
            </h3>
            <p className="text-muted-foreground mt-2 max-w-sm">
              {isAtRoot
                ? "Upload your first document to start generating study materials, or create a folder to organise your files."
                : "Add a new session here or create a sub-folder to organise your materials."}
            </p>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateFolder(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-sm font-semibold hover:bg-muted transition-colors"
              >
                <FolderPlus className="w-4 h-4" />
                New Folder
              </button>
              <Link href={newSessionHref}>
                <Button>Create Session</Button>
              </Link>
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {folders.map((folder) => (
              <FolderCard
                key={`folder-${folder.id}`}
                folder={folder}
                onClick={() => handleOpenFolder(folder)}
                onDelete={() => setDeleteTarget({ type: "folder", id: folder.id, name: folder.name, itemCount: folder.itemCount })}
                onRename={() => setRenameTarget({ type: "folder", id: folder.id, name: folder.name })}
                isDragOver={dragOverFolderId === folder.id}
                onDragOver={(e) => handleDragOverFolder(e, folder.id)}
                onDragLeave={() => setDragOverFolderId(null)}
                onDrop={(e) => handleDropOnFolder(e, folder.id)}
              />
            ))}

            {(folderSessions as StudySession[]).map((session: StudySession) => (
              <SessionCard
                key={`session-${session.id}`}
                session={session}
                getSourceIcon={getSourceIcon}
                onDelete={() => setDeleteTarget({ type: "file", id: session.id, name: session.title })}
                onRename={() => setRenameTarget({ type: "file", id: session.id, name: session.title })}
                onMove={() => setMoveTarget({ id: session.id, title: session.title, currentFolderId })}
              />
            ))}
          </div>
        )}
      </div>

      <CreateFolderModal
        open={showCreateFolder}
        onClose={() => setShowCreateFolder(false)}
        parentId={currentFolderId}
      />
      <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} currentPlan={usage?.plan} />

      <ConfirmDeleteModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title={deleteTarget?.type === "folder" ? "Delete Folder" : "Delete File"}
        message={getDeleteMessage()}
        isPending={deleteTarget?.type === "folder" ? deleteFolderPending : deleteSessionPending}
      />

      <RenameModal
        open={!!renameTarget}
        onClose={() => setRenameTarget(null)}
        onConfirm={handleConfirmRename}
        currentName={renameTarget?.name ?? ""}
        label={renameTarget?.type === "folder" ? "Folder" : "File"}
        isPending={renameTarget?.type === "folder" ? renameFolderPending : renameSessionPending}
      />

      <MoveToFolderModal
        open={!!moveTarget}
        onClose={() => setMoveTarget(null)}
        onConfirm={handleConfirmMove}
        folders={allFolders}
        currentFolderId={moveTarget?.currentFolderId ?? null}
        isPending={moveSessionPending}
        sessionTitle={moveTarget?.title ?? ""}
      />
    </div>
  );
}
